import { useState, useRef, useEffect } from "react"
import { useAuth } from "../hooks/useAuth"
import { NotificationBell } from "./NotificationBell"

type PageType = "dashboard" | "timeEntry" | "timeEntry2" | "projects" | "users" | "weeklyReport" | "weeklyReportReview" | "submissionStatus" | "submittedReports" | "quotations" | "equipmentHistory" | "specManagement" | "schedule" | "teamCalendar" | "myTodos" | "announcementManagement"

interface NavbarProps {
  currentPage: PageType
  setCurrentPage: (page: PageType) => void
  onNavigateToWeek?: (weekStart: string, weekEnd: string) => void
  isManager?: boolean
  isCLevel?: boolean
  userRole?: string
  teamName?: string
  userName?: string
}

interface WeeklyTab {
  key: PageType
  label: string
}

export function Navbar({ currentPage, setCurrentPage, onNavigateToWeek, isManager: propIsManager, isCLevel: propIsCLevel, userRole: propUserRole, teamName: propTeamName, userName: propUserName }: NavbarProps) {
  const { user, signOut, isAdmin, teamName: authTeamName, teamId, userName: authUserName, userRole: authUserRole, isCLevel: authIsCLevel, isManager: authIsManager } = useAuth()

  // prop으로 전달된 값이 있으면 우선 사용, 없으면 useAuth 값 사용
  const isManager = propIsManager !== undefined ? propIsManager : authIsManager
  const isCLevel = propIsCLevel !== undefined ? propIsCLevel : authIsCLevel
  const userRole = propUserRole || authUserRole
  const teamName = propTeamName !== undefined ? propTeamName : authTeamName
  const userName = propUserName !== undefined ? propUserName : authUserName
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [weeklyDropdownOpen, setWeeklyDropdownOpen] = useState(false)
  const [mobileWeeklyOpen, setMobileWeeklyOpen] = useState(false)
  const [equipDropdownOpen, setEquipDropdownOpen] = useState(false)
  const [mobileEquipOpen, setMobileEquipOpen] = useState(false)
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false)
  const [mobileProjectOpen, setMobileProjectOpen] = useState(false)
  const [etcDropdownOpen, setEtcDropdownOpen] = useState(false)
  const [mobileEtcOpen, setMobileEtcOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const equipDropdownRef = useRef<HTMLDivElement>(null)
  const projectDropdownRef = useRef<HTMLDivElement>(null)
  const etcDropdownRef = useRef<HTMLDivElement>(null)

  // 기타관리 드롭다운 탭 구성 (공지작성 / 제출현황) - Admin 전용
  const etcTabs: WeeklyTab[] = [
    { key: "announcementManagement", label: "공지작성" },
    { key: "submissionStatus", label: "제출현황" },
  ]
  const isEtcActive = etcTabs.some((t) => t.key === currentPage)

  // 장비관리 드롭다운 탭 구성 (제작사양서 / 장비이력) - 모든 권한 공통 노출
  const equipTabs: WeeklyTab[] = [
    { key: "specManagement", label: "제작사양서" },
    { key: "equipmentHistory", label: "장비이력" },
  ]
  const isEquipActive = equipTabs.some((t) => t.key === currentPage)

  // 프로젝트 관리 드롭다운 탭 구성 (대시보드 / 일정관리 / To do 목록)
  // - 대시보드·일정관리: 모든 권한 공통
  // - To do 목록: 본인 담당 마일스톤 모아보기이묀로 Admin / 임원(C-Level)은 미노출
  const projectTabs: WeeklyTab[] = [
    { key: "dashboard", label: "대시보드" },
    { key: "schedule", label: "일정관리" },
    { key: "teamCalendar", label: "일정표" },
  ]
  if (!isAdmin && !isCLevel) {
    projectTabs.push({ key: "myTodos", label: "To do 목록" })
  }
  const isProjectActive = projectTabs.some((t) => t.key === currentPage)

  // 권한별 주간보고 드롭다운 탭 구성
  // - 작성(weeklyReport): Admin 제외 (Admin은 작성 메뉴 없음)
  // - 검토(weeklyReportReview): Admin, C-Level, Manager
  // - 확인(submittedReports): 모든 권한
  const weeklyTabs: WeeklyTab[] = []
  if (!isAdmin) {
    weeklyTabs.push({ key: "weeklyReport", label: "주간보고 작성" })
  }
  if (isAdmin || isCLevel || isManager) {
    weeklyTabs.push({ key: "weeklyReportReview", label: "주간보고 검토" })
  }
  weeklyTabs.push({ key: "submittedReports", label: "전체 보고서 확인" })

  const isWeeklyActive = weeklyTabs.some((t) => t.key === currentPage)

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setWeeklyDropdownOpen(false)
      }
    }
    if (weeklyDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [weeklyDropdownOpen])

  // 장비관리 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (equipDropdownRef.current && !equipDropdownRef.current.contains(e.target as Node)) {
        setEquipDropdownOpen(false)
      }
    }
    if (equipDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [equipDropdownOpen])

  // 프로젝트 관리 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target as Node)) {
        setProjectDropdownOpen(false)
      }
    }
    if (projectDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [projectDropdownOpen])

  // 기타관리 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (etcDropdownRef.current && !etcDropdownRef.current.contains(e.target as Node)) {
        setEtcDropdownOpen(false)
      }
    }
    if (etcDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [etcDropdownOpen])

  const handleNotificationClick = (weekStart: string, weekEnd: string) => {
    // Manager와 User는 주간보고 관련 페이지로 이동
    if (isManager) {
      setCurrentPage("weeklyReportReview")
    } else {
      setCurrentPage("weeklyReport")
    }

    // 부모 컴포넌트에 주간 정보 전달
    if (onNavigateToWeek) {
      onNavigateToWeek(weekStart, weekEnd)
    }
  }

  const navBtnClass = (active: boolean) =>
    `px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
      active ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
    }`

  // 데스크톱 주간보고 드롭다운
  const WeeklyDropdown = () => (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setWeeklyDropdownOpen((v) => !v)}
        className={`flex items-center gap-1 ${navBtnClass(isWeeklyActive)}`}
      >
        주간보고
        <svg
          className={`w-4 h-4 transition-transform ${weeklyDropdownOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {weeklyDropdownOpen && (
        <div className="absolute left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
          {weeklyTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setCurrentPage(tab.key)
                setWeeklyDropdownOpen(false)
              }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                currentPage === tab.key
                  ? "bg-orange-100 text-orange-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // 데스크톱 프로젝트 관리 드롭다운 (대시보드 / 일정관리)
  const ProjectDropdown = () => (
    <div className="relative" ref={projectDropdownRef}>
      <button
        onClick={() => setProjectDropdownOpen((v) => !v)}
        className={`flex items-center gap-1 ${navBtnClass(isProjectActive)}`}
      >
        프로젝트 관리
        <svg
          className={`w-4 h-4 transition-transform ${projectDropdownOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {projectDropdownOpen && (
        <div className="absolute left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
          {projectTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setCurrentPage(tab.key)
                setProjectDropdownOpen(false)
              }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                currentPage === tab.key
                  ? "bg-orange-100 text-orange-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // 데스크톱 장비관리 드롭다운 (제작사양서 / 장비이력)
  const EquipmentDropdown = () => (
    <div className="relative" ref={equipDropdownRef}>
      <button
        onClick={() => setEquipDropdownOpen((v) => !v)}
        className={`flex items-center gap-1 ${navBtnClass(isEquipActive)}`}
      >
        장비관리
        <svg
          className={`w-4 h-4 transition-transform ${equipDropdownOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {equipDropdownOpen && (
        <div className="absolute left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
          {equipTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setCurrentPage(tab.key)
                setEquipDropdownOpen(false)
              }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                currentPage === tab.key
                  ? "bg-orange-100 text-orange-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // 데스크톱 기타관리 드롭다운 (공지작성 / 제출현황) - Admin 전용
  const EtcDropdown = () => (
    <div className="relative" ref={etcDropdownRef}>
      <button
        onClick={() => setEtcDropdownOpen((v) => !v)}
        className={`flex items-center gap-1 ${navBtnClass(isEtcActive)}`}
      >
        기타관리
        <svg
          className={`w-4 h-4 transition-transform ${etcDropdownOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {etcDropdownOpen && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
          {etcTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setCurrentPage(tab.key)
                setEtcDropdownOpen(false)
              }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
                currentPage === tab.key
                  ? "bg-orange-100 text-orange-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center gap-3">
            <img
              src="https://static.lumi.new/a6/a63d536d75bc6d80fe8261fd7f08abe8.webp"
              alt="PaveTech Logo"
              className="w-10 h-10 lg:w-12 lg:h-12 object-contain"
            />
            <div>
              <h1 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">프로젝트 시간 관리</h1>
              <p className="text-xs text-gray-500 hidden sm:block">PaveTech</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {isAdmin ? (
              // Admin: 대시보드 / 프로젝트 관리 / 직원 관리 / 주간보고(검토,확인) / 견적서 관리 / 제출 현황
              <nav className="flex gap-1 items-center">
                <ProjectDropdown />
                <button onClick={() => setCurrentPage("projects")} className={navBtnClass(currentPage === "projects")}>
                  프로젝트 등록
                </button>
                <button onClick={() => setCurrentPage("users")} className={navBtnClass(currentPage === "users")}>
                  직원 관리
                </button>
                <WeeklyDropdown />
                <button onClick={() => setCurrentPage("quotations")} className={navBtnClass(currentPage === "quotations")}>
                  견적서 관리
                </button>
                <EquipmentDropdown />
                <EtcDropdown />
              </nav>
            ) : (
              // C-Level / Manager / User: 홈 / 대시보드 / 주간보고 / (견적서 관리)
              <nav className="flex gap-1 items-center">
                <button onClick={() => setCurrentPage("timeEntry")} className={navBtnClass(currentPage === "timeEntry")}>
                  홈
                </button>
                <ProjectDropdown />
                <WeeklyDropdown />
                {(isCLevel || isManager) && (
                  <button onClick={() => setCurrentPage("quotations")} className={navBtnClass(currentPage === "quotations")}>
                    견적서 관리
                  </button>
                )}
                <EquipmentDropdown />
              </nav>
            )}

            <div className="flex items-center gap-4">
              {/* Notification Bell - Manager와 User만 표시 */}
              {(isManager || (!isAdmin && !isCLevel)) && (
                <NotificationBell onNavigateToReport={handleNotificationClick} />
              )}

              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500">
                  {isAdmin ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      관리자
                    </span>
                  ) : isCLevel ? (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        임원
                      </span>
                      {teamName && (
                        <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          {teamName}
                        </span>
                      )}
                    </>
                  ) : isManager ? (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                        팀장
                      </span>
                      {teamName && (
                        <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          {teamName}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        사용자
                      </span>
                      {teamName && (
                        <span className="inline-flex items-center px-2 py-0.5 ml-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          {teamName}
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
              <button
                onClick={signOut}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                로그아웃
              </button>
            </div>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-gray-200 mt-2 pt-4">
            {/* User Info */}
            <div className="px-4 py-3 bg-gray-50 rounded-lg mb-4">
              <p className="text-sm font-medium text-gray-900 mb-1">{userName}</p>
              <div className="flex gap-2 flex-wrap">
                {isAdmin ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                    관리자
                  </span>
                ) : isCLevel ? (
                  <>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      임원
                    </span>
                    {teamName && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        {teamName}
                      </span>
                    )}
                  </>
                ) : isManager ? (
                  <>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                      팀장
                    </span>
                    {teamName && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        {teamName}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      사용자
                    </span>
                    {teamName && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        {teamName}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Navigation Links */}
            <nav className="space-y-2 mb-4">
              {isAdmin ? null : (
                <button
                  onClick={() => {
                    setCurrentPage("timeEntry")
                    setMobileMenuOpen(false)
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                    currentPage === "timeEntry" ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  홈
                </button>
              )}

              {/* 프로젝트 관리 드롭다운 (모바일 - 아코디언 형태) - 대시보드 / 일정관리 */}
              <div>
                <button
                  onClick={() => setMobileProjectOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors ${
                    isProjectActive ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span>프로젝트 관리</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${mobileProjectOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileProjectOpen && (
                  <div className="mt-1 ml-3 space-y-1 border-l-2 border-orange-100 pl-2">
                    {projectTabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => {
                          setCurrentPage(tab.key)
                          setMobileMenuOpen(false)
                          setMobileProjectOpen(false)
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === tab.key
                            ? "bg-orange-100 text-orange-700"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Admin 전용: 프로젝트 등록 / 직원 관리 */}
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      setCurrentPage("projects")
                      setMobileMenuOpen(false)
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                      currentPage === "projects" ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    프로젝트 등록
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("users")
                      setMobileMenuOpen(false)
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                      currentPage === "users" ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    직원 관리
                  </button>
                </>
              )}

              {/* 주간보고 드롭다운 (모바일 - 아코디언 형태) */}
              <div>
                <button
                  onClick={() => setMobileWeeklyOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors ${
                    isWeeklyActive ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span>주간보고</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${mobileWeeklyOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileWeeklyOpen && (
                  <div className="mt-1 ml-3 space-y-1 border-l-2 border-orange-100 pl-2">
                    {weeklyTabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => {
                          setCurrentPage(tab.key)
                          setMobileMenuOpen(false)
                          setMobileWeeklyOpen(false)
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === tab.key
                            ? "bg-orange-100 text-orange-700"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 견적서 관리 - Admin / C-Level / Manager */}
              {(isAdmin || isCLevel || isManager) && (
                <button
                  onClick={() => {
                    setCurrentPage("quotations")
                    setMobileMenuOpen(false)
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                    currentPage === "quotations" ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  견적서 관리
                </button>
              )}

              {/* 장비관리 드롭다운 (모바일 - 아코디언 형태) - 모든 권한 */}
              <div>
                <button
                  onClick={() => setMobileEquipOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors ${
                    isEquipActive ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span>장비관리</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${mobileEquipOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileEquipOpen && (
                  <div className="mt-1 ml-3 space-y-1 border-l-2 border-orange-100 pl-2">
                    {equipTabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => {
                          setCurrentPage(tab.key)
                          setMobileMenuOpen(false)
                          setMobileEquipOpen(false)
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === tab.key
                            ? "bg-orange-100 text-orange-700"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 기타관리 드롭다운 (모바일 - 아코디언 형태) - Admin 전용 : 공지작성 / 제출현황 */}
              {isAdmin && (
                <div>
                  <button
                    onClick={() => setMobileEtcOpen((v) => !v)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors ${
                      isEtcActive ? "bg-orange-100 text-orange-700" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <span>기타관리</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${mobileEtcOpen ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {mobileEtcOpen && (
                    <div className="mt-1 ml-3 space-y-1 border-l-2 border-orange-100 pl-2">
                      {etcTabs.map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => {
                            setCurrentPage(tab.key)
                            setMobileMenuOpen(false)
                            setMobileEtcOpen(false)
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === tab.key
                              ? "bg-orange-100 text-orange-700"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Logout Button */}
            <button
              onClick={() => {
                signOut()
                setMobileMenuOpen(false)
              }}
              className="w-full px-4 py-3 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors"
            >
              로그아웃
            </button>
          </div>
        )}
      </div>
    </nav>
  )
}
